/**
 * BatchScanModal - Batch rescan historical events for PII
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Switch,
  Button,
  Alert,
  Progress,
  Statistic,
  Row,
  Col,
  Table,
  Tag,
  Space,
  Divider
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { startBatchRescan, getBatchRescanJob, listBatchRescanJobs } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';
import dayjs from 'dayjs';
import PIIEventsModal from './PIIEventsModal';

const { Option } = Select;
const { RangePicker } = DatePicker;

function BatchScanModal({ visible, onClose, projectId }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [polling, setPolling] = useState(false);
  const [piiEventsModalVisible, setPiiEventsModalVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    if (visible && projectId) {
      fetchJobHistory();
      // Auto-refresh job history every 5 seconds while modal is open
      const historyInterval = setInterval(() => {
        fetchJobHistory();
      }, 5000);
      return () => clearInterval(historyInterval);
    }
  }, [visible, projectId]);

  useEffect(() => {
    let interval;
    if (activeJob && activeJob.status === 'running' && polling) {
      interval = setInterval(() => {
        fetchJobStatus(activeJob.id);
      }, 2000); // Poll every 2 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeJob, polling]);

  const fetchJobHistory = async () => {
    try {
      const response = await listBatchRescanJobs(projectId);
      setJobHistory(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch job history:', error);
    }
  };

  const fetchJobStatus = async (jobId) => {
    try {
      const response = await getBatchRescanJob(projectId, jobId);
      const job = response.data;
      setActiveJob(job);

      if (job.status === 'completed' || job.status === 'failed') {
        setPolling(false);
        fetchJobHistory(); // Refresh history
      }
    } catch (error) {
      console.error('Failed to fetch job status:', error);
      setPolling(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const filters = {
        provider: values.provider || null,
        start_date: values.dateRange ? values.dateRange[0].toISOString() : null,
        end_date: values.dateRange ? values.dateRange[1].toISOString() : null,
        dry_run: values.dry_run || false
      };

      const response = await startBatchRescan(projectId, filters);
      showSuccess(values.dry_run ? 'Dry run started' : 'Batch scan started');

      const job = response.data.job;
      setActiveJob(job);
      setPolling(true);
      form.resetFields();
    } catch (error) {
      console.error('Failed to start batch scan:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!activeJob || activeJob.total_events === 0) return 0;
    return Math.round((activeJob.processed_events / activeJob.total_events) * 100);
  };

  const jobHistoryColumns = [
    {
      title: 'Job ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusConfig = {
          queued: { color: 'default', icon: <ClockCircleOutlined /> },
          running: { color: 'processing', icon: <ReloadOutlined spin /> },
          completed: { color: 'success', icon: <CheckCircleOutlined /> },
          failed: { color: 'error', icon: <WarningOutlined /> }
        };
        const config = statusConfig[status] || statusConfig.queued;
        return (
          <Tag color={config.color} icon={config.icon}>
            {status.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Total Events',
      dataIndex: 'total_events',
      key: 'total_events',
      width: 120,
      align: 'right'
    },
    {
      title: 'PII Detected',
      dataIndex: 'pii_detected_count',
      key: 'pii_detected_count',
      width: 150,
      align: 'right',
      render: (count, record) => (
        <Space>
          <Tag color={count > 0 ? 'red' : 'default'}>{count || 0}</Tag>
          {count > 0 && (
            <Button
              size="small"
              type="link"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedJob(record);
                setPiiEventsModalVisible(true);
              }}
            >
              View
            </Button>
          )}
        </Space>
      )
    },
    {
      title: 'Provider',
      dataIndex: 'provider_filter',
      key: 'provider_filter',
      width: 100,
      render: (provider) => provider || 'All'
    },
    {
      title: 'Dry Run',
      dataIndex: 'dry_run',
      key: 'dry_run',
      width: 80,
      render: (dryRun) => dryRun ? <Tag>Yes</Tag> : <Tag>No</Tag>
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => new Date(date).toLocaleString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          size="small"
          onClick={() => {
            setActiveJob(record);
            if (record.status === 'running') {
              setPolling(true);
            }
          }}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <Modal
      title="Batch PII Re-scan"
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      <Alert
        message="Batch Scanning"
        description="Re-scan historical events to detect PII using your current configuration. Use dry-run mode to preview results without modifying events."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Start New Scan Form */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          dry_run: false
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Provider Filter (Optional)"
              name="provider"
            >
              <Select
                placeholder="All providers"
                allowClear
              >
                <Option value="openai">OpenAI</Option>
                <Option value="anthropic">Anthropic</Option>
                <Option value="bedrock">AWS Bedrock</Option>
                <Option value="github">GitHub</Option>
                <Option value="slack">Slack</Option>
                <Option value="datadog">Datadog</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Date Range (Optional)"
              name="dateRange"
            >
              <RangePicker
                style={{ width: '100%' }}
                showTime
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Dry Run Mode"
          name="dry_run"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="Dry Run"
            unCheckedChildren="Live Scan"
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            icon={<ReloadOutlined />}
          >
            Start Batch Scan
          </Button>
        </Form.Item>
      </Form>

      <Divider />

      {/* Active Job Progress */}
      {activeJob && (
        <div style={{ marginBottom: 24 }}>
          <h3>Current Job Status</h3>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Statistic
                title="Status"
                value={activeJob.status}
                valueStyle={{
                  color: activeJob.status === 'completed' ? '#3f8600' :
                    activeJob.status === 'failed' ? '#cf1322' :
                      activeJob.status === 'running' ? '#1890ff' : '#666'
                }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Total Events"
                value={activeJob.total_events || 0}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Processed"
                value={activeJob.processed_events || 0}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="PII Detected"
                value={activeJob.pii_detected_count || 0}
                valueStyle={{ color: '#cf1322' }}
              />
              {activeJob.pii_detected_count > 0 && (
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    setSelectedJob(activeJob);
                    setPiiEventsModalVisible(true);
                  }}
                  style={{ marginTop: 8 }}
                >
                  View Events
                </Button>
              )}
            </Col>
          </Row>

          {activeJob.status === 'running' && (
            <Progress
              percent={calculateProgress()}
              status="active"
              format={(percent) => `${percent}% (${activeJob.processed_events}/${activeJob.total_events})`}
            />
          )}

          {activeJob.status === 'completed' && (
            <Alert
              message="Scan Complete"
              description={`Successfully scanned ${activeJob.total_events} events. Found PII in ${activeJob.pii_detected_count} events.`}
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {activeJob.status === 'failed' && (
            <Alert
              message="Scan Failed"
              description={activeJob.error_message || 'An unknown error occurred'}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {activeJob.dry_run && (
            <Alert
              message="Dry Run Mode"
              description="This was a preview scan. No events were modified."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      )}

      {/* Job History */}
      <div>
        <h3>Recent Jobs</h3>
        <Table
          dataSource={jobHistory}
          columns={jobHistoryColumns}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          size="small"
          scroll={{ x: 800 }}
        />
      </div>

      {/* PII Events Modal */}
      {selectedJob && (
        <PIIEventsModal
          visible={piiEventsModalVisible}
          onClose={() => {
            setPiiEventsModalVisible(false);
            setSelectedJob(null);
          }}
          projectId={projectId}
          connectorId={selectedJob.connector_id}
        />
      )}
    </Modal>
  );
}

export default BatchScanModal;
